import { get } from 'svelte/store';
import { storyStore } from '../stores/storyStore.js';

function sanitizeFilename(title) {
    // Replace invalid filename characters with underscores and trim whitespace
    return title
        .replace(/[/\\?%*:|"<>]/g, '_')
        .replace(/\s+/g, '_')
        .trim() || 'story';
}

export async function shareStory(jsPDF, progressCallback) {
    console.log('Sharing story... via shareStory()');
    
    // Setup progress reporting
    const updateProgress = (message, percent) => {
        if (typeof progressCallback === 'function') {
            progressCallback(message, percent);
        }
    };
    
    updateProgress('Initializing PDF...', 0);
    
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [375, 667] // Match canvas dimensions
    });

    try {
        // Get all slides and story title from the storyStore
        const currentStory = get(storyStore);
        const slides = currentStory.slides;
        const filename = sanitizeFilename(currentStory.title);
        
        updateProgress('Preparing slides...', 5);

        // Set some styling constants
        const margin = 20;
        const scriptFontSize = 12;
        doc.setFontSize(scriptFontSize);
        
        // Track total slide count for progress calculation
        const totalSlides = slides.length;
        const progressPerSlide = 90 / Math.max(totalSlides, 1); // 90% of progress for slides

        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            updateProgress(`Processing slide ${i+1} of ${totalSlides}...`, 5 + (i * progressPerSlide));

            // Create a temporary canvas for each slide
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = 375;
            tempCanvas.height = 667;
            const fabricCanvas = new window.fabric.Canvas(tempCanvas);

            // Add a new page for slides after the first one
            if (i > 0) {
                doc.addPage();
            }

            // Load and render the canvas state
            if (slide.canvasState && slide.canvasState.length > 0) {
                try {
                    updateProgress(`Rendering slide ${i+1} content...`, 5 + (i * progressPerSlide) + (progressPerSlide * 0.3));
                    
                    // Pre-process canvas state to handle clipboard images
                    const processedState = slide.canvasState.map(obj => {
                        // Create a new object to avoid modifying the original
                        const newObj = {...obj};
                        
                        // Check if this is a clipboard image that needs special handling
                        if (newObj.type === 'image' && 
                            newObj.metadata && 
                            newObj.metadata.isClipboardImage) {
                            
                            console.log('Processing clipboard image for PDF export in shareStory');
                            
                            // Check for valid and non-truncated data URL sources
                            if (typeof newObj.src === 'string' && newObj.src.startsWith('data:image')) {
                                // Check if the src looks truncated or corrupted
                                if (newObj.src.includes('compressed') || newObj.src.length < 1000) {
                                    console.log('Data URL appears to be truncated or corrupted in exportService');
                                    
                                    // Try to get the embedded data URL from metadata if available
                                    if (newObj.metadata && newObj.metadata.embeddedDataUrl) {
                                        console.log('Using embedded data URL from metadata in exportService');
                                        newObj.src = newObj.metadata.embeddedDataUrl;
                                    } else {
                                        console.log('No valid data URL available in exportService, creating placeholder');
                                        // Replace with a colored rectangle as placeholder
                                        newObj.type = 'rect';
                                        newObj.fill = '#e2e8f0';
                                        newObj.stroke = '#cbd5e1';
                                        newObj.strokeWidth = 2;
                                        delete newObj.src;
                                    }
                                } else {
                                    console.log('Using existing data URL for clipboard image in exportService');
                                }
                            } 
                            // If the src is a blob URL or missing, check for embedded data or create placeholder
                            else if (typeof newObj.src === 'string' && (newObj.src.startsWith('blob:') || newObj.src === '')) {
                                console.log('Replacing blob URL with placeholder in exportService:', newObj.src);
                                
                                // Try to use embedded data URL first if available
                                if (newObj.metadata && newObj.metadata.embeddedDataUrl) {
                                    console.log('Using embedded data URL from metadata instead of blob URL in exportService');
                                    newObj.src = newObj.metadata.embeddedDataUrl;
                                } else {
                                    // Replace with a colored rectangle as placeholder
                                    newObj.type = 'rect';
                                    newObj.fill = '#e2e8f0';
                                    newObj.stroke = '#cbd5e1'; 
                                    newObj.strokeWidth = 2;
                                    newObj.width = newObj.width || 100;
                                    newObj.height = newObj.height || 100;
                                    delete newObj.src;
                                }
                            }
                        }
                        
                        return newObj;
                    });
                    
                    // Add a white background to ensure visibility
                    const backgroundRect = new window.fabric.Rect({
                        left: 0,
                        top: 0,
                        width: fabricCanvas.width,
                        height: fabricCanvas.height,
                        fill: 'white',
                        selectable: false,
                        evented: false,
                    });
                    
                    updateProgress(`Loading canvas for slide ${i+1}...`, 5 + (i * progressPerSlide) + (progressPerSlide * 0.6));
                    
                    // Set a timeout to prevent hanging on complex slides
                    const renderPromise = new Promise((resolve) => {
                        fabricCanvas.loadFromJSON(
                            { version: '5.2.4', objects: processedState },
                            () => {
                                // Add the background rectangle and send it to back
                                fabricCanvas.add(backgroundRect);
                                fabricCanvas.sendToBack(backgroundRect);
                                
                                fabricCanvas.renderAll();
                                resolve();
                            }
                        );
                    });
                    
                    // Add a timeout to prevent hanging
                    const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(() => reject(new Error('Canvas rendering timed out')), 20000);
                    });
                    
                    // Race the rendering against the timeout
                    await Promise.race([renderPromise, timeoutPromise]);
                    
                    updateProgress(`Generating image for slide ${i+1}...`, 5 + (i * progressPerSlide) + (progressPerSlide * 0.8));
                    
                    // Convert the canvas to an image
                    const imgData = tempCanvas.toDataURL('image/png');
                    
                    // Add the image to the PDF
                    doc.addImage(imgData, 'PNG', 0, 0, 375, 667);
                    
                    updateProgress(`Completed slide ${i+1}...`, 5 + (i * progressPerSlide) + progressPerSlide);
                } catch (error) {
                    console.error('Error rendering canvas for PDF export:', error);
                    
                    // Create a fallback - plain white page with error message
                    const ctx = tempCanvas.getContext('2d');
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                    
                    ctx.fillStyle = 'red';
                    ctx.font = '16px Arial';
                    ctx.fillText(`Error rendering slide ${i + 1}`, 20, 50);
                    ctx.font = '12px Arial';
                    ctx.fillText(`${error.message || 'Unknown error'}`, 20, 70);
                    
                    // Add the error canvas to the PDF
                    const errorImgData = tempCanvas.toDataURL('image/png');
                    doc.addImage(errorImgData, 'PNG', 0, 0, 375, 667);
                }
            } else {
                // If no canvas state, create a blank white canvas
                const ctx = tempCanvas.getContext('2d');
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                
                // Add the blank canvas to the PDF
                const blankImgData = tempCanvas.toDataURL('image/png');
                doc.addImage(blankImgData, 'PNG', 0, 0, 375, 667);
            }

            // Add the script text if it exists
            if (slide.script) {
                // Add a new page for the script
                doc.addPage();
                
                // Add slide number and "Script" header
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text(`Slide ${i + 1} Script:`, margin, margin + 10);
                
                // Reset font for script content
                doc.setFontSize(scriptFontSize);
                doc.setFont(undefined, 'normal');
                
                // Split text into lines that fit within page width
                const textLines = doc.splitTextToSize(slide.script, 375 - (margin * 2));
                
                // Add the script text with proper line breaks
                doc.text(textLines, margin, margin + 30);
            }
        }

        updateProgress('Finalizing document...', 95);
        
        // Save the PDF with the story title
        doc.save(`${filename}.pdf`);
        
        updateProgress('PDF generated successfully', 100);
        console.log('Story exported to PDF successfully');
    } catch (error) {
        console.error('Error exporting story to PDF:', error);
        throw error; // Re-throw to allow caller to handle
    }
}
